import { type } from 'arktype';

// ---------------------------------------------------------------------------
// Enums / constants
// ---------------------------------------------------------------------------

export const TEMPLATE_STATUSES = ['active', 'inactive'] as const;
export type TemplateStatus = (typeof TEMPLATE_STATUSES)[number];

export const TEMPLATE_VERSION_STATUSES = ['draft', 'active', 'archived'] as const;
export type TemplateVersionStatus = (typeof TEMPLATE_VERSION_STATUSES)[number];

export const TEMPLATE_ENGINES = ['nunjucks'] as const;
export type TemplateEngine = (typeof TEMPLATE_ENGINES)[number];

export const TEMPLATE_MEDIUMS = ['pdf', 'email', 'html', 'docx'] as const;
export type TemplateMedium = (typeof TEMPLATE_MEDIUMS)[number];

// ---------------------------------------------------------------------------
// Template version content shape
// ---------------------------------------------------------------------------

export interface TemplateContent {
  body: string;
  style?: string;
}

// ---------------------------------------------------------------------------
// Create / Update schemas (ArkType)
// ---------------------------------------------------------------------------

export const createTemplateSchema = type({
  key: 'string >= 1',
  name: 'string >= 1',
  'defaultMedium?': '"pdf" | "email" | "html" | "docx"',
});

export type CreateTemplate = typeof createTemplateSchema.t;

export const updateTemplateSchema = type({
  'name?': 'string >= 1',
  'defaultMedium?': '"pdf" | "email" | "html" | "docx" | null',
});

export type UpdateTemplate = typeof updateTemplateSchema.t;

export const createTemplateVersionSchema = type({
  engine: '"nunjucks"',
  schema: 'Record<string, unknown>',
  content: {
    body: 'string >= 1',
    'style?': 'string',
  },
});

export type CreateTemplateVersion = typeof createTemplateVersionSchema.t;

// ---------------------------------------------------------------------------
// PDF generation job data / response
// ---------------------------------------------------------------------------

export const submitPdfSchema = type({
  templateId: 'string >= 1',
  'templateVersion?': 'number',
  'templateVersionId?': 'string.uuid',
  inputData: 'Record<string, unknown>',
});

export type SubmitPdfRequest = typeof submitPdfSchema.t;

// ---------------------------------------------------------------------------
// Response shapes
// ---------------------------------------------------------------------------

export interface TemplateVersionResponse {
  id: string;
  templateId: string;
  version: number;
  status: TemplateVersionStatus;
  engine: TemplateEngine;
  schema: Record<string, unknown>;
  content: TemplateContent;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface TemplateResponse {
  id: string;
  tenantId: string;
  key: string;
  name: string;
  status: TemplateStatus;
  defaultMedium: string | null;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  versions?: TemplateVersionResponse[];
}

export interface PdfSubmitResponse {
  id: string;
  status: 'pending';
}

export interface GeneratedDocumentResponse {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  medium: 'pdf';
  templateId: string;
  templateVersionId: string;
  requestedBy: string;
  createdAt: Date;
  updatedAt: Date;
  completedAt: Date | null;
  outputMeta: Record<string, unknown> | null;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Job data (passed to the BullMQ processor)
// ---------------------------------------------------------------------------

export interface PdfJobData extends Record<string, unknown> {
  documentId: string;
  templateVersionId: string;
  inputData: Record<string, unknown>;
  requestedBy: string;
  tenantId?: string;
  /** Database connection URL for the tenant. Auto-injected by JobsService.submitJob(). */
  databaseUrl?: string;
}

export interface PdfJobResult {
  documentId: string;
  outputMeta: {
    filename: string;
    size: number;
    mime: string;
  };
  processingTimeMs: number;
}
